import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { centsToCurrency } from "../../utils/money";
import { formatDate } from "../../utils/date";
import type { Payment } from "../../../types";
import { api } from "../../../api";
import { useEffect, useState } from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { clientDetailsLabels } from "../labels";
import { MoreHorizontal, Trash2, Undo2, Calendar as CalendarIcon, ExternalLink, Check, CreditCard, DollarSign } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Calendar } from "../../../components/ui/calendar";
import { navigate } from "../../../lib/router";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";

interface Props {
  payments: Payment[];
  onUpdated: (p: Payment) => void;
  onRefreshed?: (list: Payment[]) => void;
}

export function PaymentsSection({ payments, onUpdated, onRefreshed }: Props) {
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Payment | null>(null);
  const [editDue, setEditDue] = useState<Payment | null>(null);
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [dueTime, setDueTime] = useState<string>("");
  const { lang } = useLanguage();
  const t = clientDetailsLabels[lang].paymentsSection;

  useEffect(() => {
    console.log('[PaymentsSection] payments', payments);
  }, [payments]);

  const markAsPaid = async (p: Payment) => {
    setLoadingId(p.id);
    const body: Partial<{ status: 'pending' | 'paid' }> = { status: 'paid' };
    const res = await api.updatePayment(p.id, body);
    setLoadingId(null);
    if (res.ok) {
      const updated = res.data as Payment;
      onUpdated(updated);
      const refreshed = await api.listClientPayments(p.client_id);
      if (refreshed.ok) {
        if (onRefreshed) {
          onRefreshed(refreshed.data);
        } else {
          for (const item of refreshed.data) {
            if (item.id !== updated.id) onUpdated(item);
          }
        }
      }
    }
  };

  const unmarkPaid = async (p: Payment) => {
    const mysqlDate = p.paid_at ? new Date(p.paid_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    const res = await api.updatePayment(p.id, { status: 'pending', paid_at: mysqlDate });
    setLoadingId(null);
    if (res.ok) {
      const updated = { ...p, ...(res.data as any), status: 'pending' };
      onUpdated(updated);
      const refreshed = await api.listClientPayments(p.client_id);
      if (refreshed.ok) {
        if (onRefreshed) {
          onRefreshed(refreshed.data);
        } else {
          for (const item of refreshed.data) {
            if (item.id !== updated.id) onUpdated(item);
          }
        }
      }
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const p = confirmDelete;
    setLoadingId(p.id);
    const res = await api.deletePayment(p.id);
    setLoadingId(null);
    setConfirmDelete(null);
    if ((res as any).ok !== false) {
      onUpdated({ ...p, total_amount_cents: -1 } as any);
    }
  };

  const openEditDue = (p: Payment) => {
    setMenuOpenId(null);
    setEditDue(p);
    const d = new Date(String(p.paid_at || '').replace(' ', 'T'));
    if (!isNaN(d.getTime())) {
      setDueDate(d);
      if (p.status === 'paid' && !p.recurring_plan_id) {
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        setDueTime(`${hh}:${mm}`);
      } else {
        setDueTime("");
      }
    } else {
      setDueDate(new Date());
      setDueTime(p.status === 'paid' && !p.recurring_plan_id ? "09:00" : "");
    }
  };

  const saveEditDue = async () => {
    if (!editDue) return;
    if (!dueDate) return;
    if (editDue.status === 'paid' && !editDue.recurring_plan_id && !dueTime) return;
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = dueDate.getFullYear();
    const mm = pad(dueDate.getMonth() + 1);
    const dd = pad(dueDate.getDate());
    const mysql = `${yyyy}-${mm}-${dd} 00:00:00`;
    setLoadingId(editDue.id);
    const res = await api.updatePayment(editDue.id, mysql ? { paid_at: mysql } as any : {} as any);
    setLoadingId(null);
    if (res.ok) {
      onUpdated({ ...editDue, ...(res.data as any) });
      setEditDue(null);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden flex flex-col">
      <div className="p-6 border-b border-neutral-50 flex items-center justify-between bg-gradient-to-r from-white to-neutral-50/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
            <DollarSign className="w-4 h-4" />
          </div>
          <span className="font-bold text-neutral-800 tracking-tight">{t.title}</span>
        </div>
      </div>

      <div className="p-4">
        {payments.length === 0 ? (
          <div className="py-12 text-center flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-neutral-50 flex items-center justify-center text-neutral-300">
              <CreditCard className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-neutral-400 italic">{t.empty}</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-neutral-50 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)]">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-neutral-50">
                  <TableHead className="uppercase text-[9px] font-bold tracking-widest text-neutral-400 pl-6 h-12">{t.columns.date}</TableHead>
                  <TableHead className="uppercase text-[9px] font-bold tracking-widest text-neutral-400 h-12">{t.columns.amount}</TableHead>
                  <TableHead className="uppercase text-[9px] font-bold tracking-widest text-neutral-400 h-12">{t.columns.status}</TableHead>
                  <TableHead className="hidden lg:table-cell uppercase text-[9px] font-bold tracking-widest text-neutral-400 h-12">{t.columns.plan}</TableHead>
                  <TableHead className="text-right uppercase text-[9px] font-bold tracking-widest text-neutral-400 pr-6 h-12">{t.columns.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map(p => (
                  <TableRow key={p.id} className="border-neutral-50 hover:bg-neutral-50/[0.3] transition-colors group">
                    <TableCell className="pl-6 py-4">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="h-3 w-3 text-neutral-300" />
                          <span className="text-[9px] font-bold uppercase text-neutral-300 tracking-tight">
                            {lang === 'pt' ? "Vencimento" : "Due Date"}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-neutral-700 tabular-nums">
                          {formatDate(p.due_date || (p.status === 'pending' ? p.paid_at : p.period_start || p.paid_at))}
                        </span>
                        {p.status === 'paid' && (
                          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 rounded-full w-fit">
                            <Check className="h-2.5 w-2.5 text-emerald-600" />
                            <span className="text-[9px] font-bold uppercase text-emerald-600 tracking-tight">
                              {lang === 'pt' ? "Pago em" : "Paid on"}: {formatDate(p.paid_at)}
                            </span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <span className="text-lg font-bold text-neutral-900 tabular-nums tracking-tighter">
                        {centsToCurrency(p.total_amount_cents, lang)}
                      </span>
                    </TableCell>
                    <TableCell className="py-4">
                      <Badge
                        variant={p.status === 'paid' ? 'success' : 'warning'}
                        className="text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-lg"
                      >
                        {p.status === 'paid' ? t.status.paid : t.status.pending}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell py-4">
                      {p.recurring_plan_id && p.plan_name ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/app/plans/${p.recurring_plan_id}`)}
                          className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-primary hover:text-white bg-primary/5 hover:bg-primary px-3 py-1.5 rounded-xl transition-all"
                          aria-label={t.actions.openPlan}
                        >
                          {p.plan_name}
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      ) : (
                        <span className="text-[10px] font-bold text-neutral-300 uppercase tracking-widest">{t.planFallback}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right pr-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {p.status !== 'paid' ? (
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-9 w-9 rounded-xl border-emerald-100 bg-emerald-50/50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all active:scale-90"
                            onClick={() => markAsPaid(p)}
                            disabled={loadingId === p.id}
                            icon={<Check className="h-4.5 w-4.5" />}
                            title={t.actions.markAsPaid}
                          />
                        ) : (
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-9 w-9 rounded-xl border-neutral-100 bg-neutral-50/50 text-neutral-400 hover:bg-neutral-600 hover:text-white transition-all active:scale-90"
                            onClick={() => unmarkPaid(p)}
                            disabled={loadingId === p.id}
                            icon={<Undo2 className="h-4.5 w-4.5" />}
                            title={t.actions.unmarkPayment}
                          />
                        )}

                        <Popover
                          open={menuOpenId === p.id}
                          onOpenChange={(open) => setMenuOpenId(open ? p.id : null)}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 rounded-xl hover:bg-neutral-100 border border-transparent group-hover:border-neutral-100 transition-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenId(id => (id === p.id ? null : p.id));
                              }}
                              icon={<MoreHorizontal className="h-4 w-4" />}
                              aria-label={t.actions.moreActions}
                            />
                          </PopoverTrigger>
                          <PopoverContent align="end" sideOffset={8} className="w-52 p-2 rounded-2xl shadow-2xl border-neutral-100 bg-white/80 backdrop-blur-xl">
                            <div className="flex flex-col gap-1">
                              <button
                                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-neutral-50 font-bold text-[11px] uppercase tracking-widest text-neutral-600 transition-colors"
                                onClick={() => {
                                  setMenuOpenId(null);
                                  openEditDue(p);
                                }}
                              >
                                <CalendarIcon className="h-4 w-4 text-neutral-400" /> {t.actions.editDue}
                              </button>
                              <div className="h-px bg-neutral-50 my-1 mx-2" />
                              <button
                                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-red-500 hover:bg-red-50 font-bold text-[11px] uppercase tracking-widest transition-colors"
                                onClick={() => {
                                  setMenuOpenId(null);
                                  setConfirmDelete(p);
                                }}
                              >
                                <Trash2 className="h-4 w-4" /> {t.actions.delete}
                              </button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={!!editDue} onOpenChange={(o) => !o && setEditDue(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold uppercase tracking-widest text-neutral-800">{t.dialogs.editDue.title}</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <Calendar 
              selected={dueDate} 
              onSelect={setDueDate}
              className="rounded-2xl border-neutral-100"
            />
          </div>
          <DialogFooter className="p-4 bg-neutral-50 rounded-b-3xl gap-2">
            <Button variant="ghost" onClick={() => setEditDue(null)} className="h-10 text-[10px] font-bold uppercase tracking-widest rounded-xl">
              {t.dialogs.editDue.cancel}
            </Button>
            <Button onClick={saveEditDue} disabled={loadingId === (editDue?.id || -1)} className="h-10 text-[10px] font-bold uppercase tracking-widest rounded-xl px-6">
              {t.dialogs.editDue.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-3xl border-0 shadow-2xl">
          <AlertDialogHeader className="text-left p-2">
            <AlertDialogTitle className="text-lg font-bold text-neutral-800 uppercase tracking-widest">{t.dialogs.delete.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm font-medium text-neutral-500 leading-relaxed pt-2">
              {t.dialogs.delete.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="p-2 gap-2">
            <AlertDialogCancel className="h-11 rounded-2xl text-[10px] font-bold uppercase tracking-widest border-neutral-100 hover:bg-neutral-50">{t.dialogs.delete.cancel}</AlertDialogCancel>
            <AlertDialogAction className="h-11 rounded-2xl text-[10px] font-bold uppercase tracking-widest bg-red-500 hover:bg-red-600" onClick={doDelete}>
              {t.dialogs.delete.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
